import type { EntitlementKey } from '../../services/feature-flag-store.service';
import {
  AI_IDE_TOUR_ID,
  DASHBOARD_TOUR_ID,
  DEFAULT_GUIDED_SETUP_STEPS,
  dashboardIntentTourSteps,
  type GuidedSetupStep,
  type GuidedTourFlagKey,
} from './guided-setup-steps';
import {
  EXPLOITS_TOUR_ID,
  EXPLOITS_TOUR_STEPS,
  POLICIES_TOUR_ID,
  POLICIES_TOUR_STEPS,
  SBOM_TOUR_ID,
  SBOM_TOUR_STEPS,
  THREAT_MODEL_TOUR_ID,
  THREAT_MODEL_TOUR_STEPS,
  WORKSPACE_TOUR_ID,
  WORKSPACE_TOUR_STEPS,
} from './product-tour-steps';
import {
  RISKS_CONTAINER_TOUR_STEPS,
  RISKS_SCA_TOUR_STEPS,
} from '../../pages/risks-tour-steps';
import {
  POLICY_MANAGEMENT_TOUR_ID,
  POLICY_MANAGEMENT_TOUR_STEPS,
  SCHEDULE_SCAN_TOUR_ID,
  SCHEDULE_SCAN_TOUR_STEPS,
} from '../../pages/policy-schedule-tour-steps';
import { AUTO_SCAN_TOUR_ID, AUTO_SCAN_TOUR_STEPS } from '../../pages/auto-scan-tour-steps';
import { HOOK_POLICY_TOUR_ID, HOOK_POLICY_TOUR_STEPS } from '../../pages/hook-policy-tour-steps';
import {
  REMEDIATION_POLICY_TOUR_ID,
  REMEDIATION_POLICY_TOUR_STEPS,
} from '../../pages/remediation-policy-tour-steps';
import { PR_SCANS_TOUR_ID, PR_SCANS_TOUR_STEPS } from '../../pages/pr-scans-tour-steps';

/** Tour id of the risks-container tour's per-user seen state. */
export const RISKS_CONTAINER_TOUR_ID = 'risks-container';
/** Tour id of the risks-sca tour's per-user seen state. */
export const RISKS_SCA_TOUR_ID = 'risks-sca';

/** One tour, and everything needed to decide whether and where to offer it. */
export interface TourDefinition {
  /**
   * Stable id, also the `user_tour_seen.tour_id` value. MUST be present in
   * `UserTourController.ALLOWED_TOUR_IDS` — an id that is not is rejected server-side with 400,
   * so the tour would re-offer itself every session.
   */
  readonly id: string;
  /** Human name, shown in the help menu. Not a sentence — it sits in a list. */
  readonly label: string;
  /**
   * Where this tour lives, as the help menu names it to a user who is somewhere else — the object
   * of "Opens …" / "Open … first" ("Exploit Hunt", "a workspace"). Lower-case unless a proper name.
   */
  readonly where: string;
  /**
   * The URL the help menu sends the user to before starting this tour from another page. Absent
   * when no static destination exists — `/workspace/:id` needs an id this menu cannot choose, and
   * the threat-model panel is not a route at all — in which case the menu says so instead of
   * pretending to navigate. When present it MUST be on one of `routes` (test-pinned), or the host
   * would arrive somewhere the tour does not claim and refuse to open.
   */
  readonly navigateTo?: string;
  readonly steps: readonly GuidedSetupStep[];
  /**
   * Route path prefixes this tour belongs to, matched SEGMENT-WISE — `/risks/sca` matches
   * `/risks/sca` and `/risks/sca/anything`, and never `/risks/scan`. An empty list means the tour
   * has no route of its own and is reachable only by explicit replay.
   */
  readonly routes: readonly string[];
  /** A global flag the WHOLE tour depends on (distinct from a single step's `requiresFlag`). */
  readonly requiresFlag?: GuidedTourFlagKey;
  /** A per-org entitlement the whole tour depends on. */
  readonly requiresEntitlement?: EntitlementKey;
  /**
   * `true` when a PAGE owns the overlay for this tour and drives its own auto-open.
   *
   * The global host never auto-opens a host-driven tour — that would double up with the page's
   * own `[autoOpen]="true"` — but it still offers it for replay from the help menu, because the
   * overlay resolves anchors from the live DOM and does not care which component instance drives
   * it. The four tours that predate the global host are all host-driven; every tour added since
   * is owned by the host, which is the cheaper shape (no per-page trigger, no per-page overlay).
   */
  readonly hostDriven?: boolean;
}

/**
 * Every guided tour in the product, in the order the help menu lists them.
 *
 * ## Why a registry instead of nine page hosts
 *
 * The four original tours each hard-wired their own overlay, their own trigger button and their
 * own auto-open into a page component — about forty lines per page, four slightly different
 * shapes, and one of them (`start.ts`) got the re-open call wrong in a way that only a
 * self-healing effect in the overlay hid. Anything a page must repeat, a page will eventually
 * repeat wrongly. `GuidedTourHostComponent` reads this table once, and a new tour is an entry
 * here plus `data-tour-step` attributes on real elements.
 *
 * ## Two invariants, both test-pinned
 *
 * 1. Every `id` here appears in `UserTourController.ALLOWED_TOUR_IDS`. There is deliberately no
 *    generated artifact shared with the backend: that allow-list's whole value is being fixed and
 *    reviewed, and a generated list is one indirection away from being derived from something a
 *    caller can influence.
 * 2. Ids are unique, and no two tours claim the same route — a route with two candidate tours has
 *    no defined auto-open order, so the ambiguity is a defect rather than a preference.
 */
export const TOUR_REGISTRY: readonly TourDefinition[] = [
  {
    id: DASHBOARD_TOUR_ID,
    label: 'Dashboard — first steps',
    where: 'the Dashboard',
    navigateTo: '/dashboard',
    steps: dashboardIntentTourSteps,
    routes: ['/dashboard'],
    // Two of its three anchors are rendered by the SIDEBAR, and the host waits for its own
    // resolved seen-state before opening, so the page keeps ownership.
    hostDriven: true,
  },
  {
    id: WORKSPACE_TOUR_ID,
    label: 'Workspace — what this screen is',
    // `/workspace/:id` — no id this menu can pick, so no `navigateTo`: the menu says
    // "Open a workspace first" rather than sending the user to a URL that does not exist.
    where: 'a workspace',
    steps: WORKSPACE_TOUR_STEPS,
    routes: ['/workspace'],
  },
  {
    id: EXPLOITS_TOUR_ID,
    label: 'Exploit Hunt — how it works',
    where: 'Exploit Hunt',
    navigateTo: '/risks/exploits',
    steps: EXPLOITS_TOUR_STEPS,
    routes: ['/risks/exploits'],
    requiresEntitlement: 'exploitsHunt',
  },
  {
    id: SBOM_TOUR_ID,
    label: 'SBOM inventory — reading the list',
    where: 'the SBOM inventory',
    navigateTo: '/inventory/sbom',
    steps: SBOM_TOUR_STEPS,
    routes: ['/inventory/sbom'],
  },
  {
    id: THREAT_MODEL_TOUR_ID,
    label: 'Threat modelling — running one',
    where: "a workspace's Threat Model panel",
    steps: THREAT_MODEL_TOUR_STEPS,
    // Same route as the workspace tour is NOT allowed (invariant 2), and this surface is a panel
    // rather than a route — so it declares none and is reached by replay, or auto-opened by the
    // host's anchor watcher at the moment the panel renders.
    routes: [],
  },
  {
    id: POLICIES_TOUR_ID,
    label: 'Policies — what a scan does',
    where: 'Policies',
    navigateTo: '/admin/policies',
    steps: POLICIES_TOUR_STEPS,
    routes: ['/admin/policies'],
  },
  {
    id: RISKS_CONTAINER_TOUR_ID,
    label: 'Container findings — the toolbar',
    where: 'Container findings',
    navigateTo: '/risks/container',
    steps: RISKS_CONTAINER_TOUR_STEPS,
    routes: ['/risks/container'],
    hostDriven: true,
  },
  {
    id: RISKS_SCA_TOUR_ID,
    label: 'Dependency findings — the toolbar',
    where: 'Dependency findings',
    navigateTo: '/risks/sca',
    steps: RISKS_SCA_TOUR_STEPS,
    routes: ['/risks/sca'],
    hostDriven: true,
  },
  {
    // The org-wide PR Policy page. A DIFFERENT tour from `policies` above, which belongs to the
    // legacy `/admin/policies` route — invariant 2 forbids two tours claiming one route, and these
    // two claim different ones. When `/admin/policies` is eventually retired, that tour and this
    // one should be reconciled rather than left as a pair.
    //
    // Repointed 2026-09-12 when the page moved to Settings -> Scan policy. The OLD
    // `/policies` path is a redirect now, and it is deliberately NOT listed in `routes`: a tour's
    // `routes` decide which URL the host is ON, and a redirect URL is never the one the router
    // settles at — claiming it would be claiming a route that can never be current.
    id: POLICY_MANAGEMENT_TOUR_ID,
    label: 'PR Policy — what decides a gate',
    where: 'PR Policy',
    navigateTo: '/settings/scan-policy/pr-policy',
    steps: POLICY_MANAGEMENT_TOUR_STEPS,
    routes: ['/settings/scan-policy/pr-policy'],
    requiresEntitlement: 'policies',
  },
  {
    // Repointed 2026-09-12 with its page — see the note above for why the old path is not listed.
    id: SCHEDULE_SCAN_TOUR_ID,
    label: 'Scheduled scans — how a window runs',
    where: 'Scheduled scans',
    navigateTo: '/settings/scan-policy/scheduled-scans',
    steps: SCHEDULE_SCAN_TOUR_STEPS,
    routes: ['/settings/scan-policy/scheduled-scans'],
  },
  {
    // Auto-scan after import (2026-09-15) — the third Scan-policy tab. Always available and
    // host-owned like its two siblings — no per-page trigger.
    id: AUTO_SCAN_TOUR_ID,
    label: 'Initial scans — what runs after an import',
    where: 'Auto-scan after import',
    navigateTo: '/settings/scan-policy/auto-scan',
    steps: AUTO_SCAN_TOUR_STEPS,
    routes: ['/settings/scan-policy/auto-scan'],
  },
  {
    // Hook policy (2026-10-21) — the organization and workspace hook policy tab. Host-owned like its
    // Scanning siblings; offered only with the `enforcementSessionGate` entitlement, the key the route
    // and the enforcement policy API are gated on.
    id: HOOK_POLICY_TOUR_ID,
    label: 'Hook policy — what local hooks check',
    where: 'Hook policy',
    navigateTo: '/settings/scan-policy/hook-policy',
    steps: HOOK_POLICY_TOUR_STEPS,
    routes: ['/settings/scan-policy/hook-policy'],
    requiresEntitlement: 'enforcementSessionGate',
  },
  {
    // Remediation Policy page, Settings -> Remediation policy (2026-09-30; own route 2026-10-01). Host-owned like the
    // Scan-policy tours; offered only to an org with the Remediation Agent entitlement, the same
    // key the panel and its API are gated on.
    id: REMEDIATION_POLICY_TOUR_ID,
    label: 'Remediation policy — automatic plans',
    where: 'Settings → Remediation policy',
    navigateTo: '/settings/remediation-policy',
    steps: REMEDIATION_POLICY_TOUR_STEPS,
    routes: ['/settings/remediation-policy'],
  },
  {
    // Overview -> PR scans (2026-10-08). Host-owned; offered only to an org holding the `prScans`
    // entitlement, the key the page and its API are gated on. Its route prefix also covers the scan
    // detail and Waivers routes under `/pr-scans`, where none of its (all-anchored) steps render, so it never
    // opens there.
    id: PR_SCANS_TOUR_ID,
    label: 'PR scans — reading the list',
    where: 'PR scans',
    navigateTo: '/pr-scans',
    steps: PR_SCANS_TOUR_STEPS,
    routes: ['/pr-scans'],
    requiresEntitlement: 'prScans',
  },
  {
    id: AI_IDE_TOUR_ID,
    label: 'Developer setup — installing it',
    where: 'AI IDE integration settings',
    navigateTo: '/settings/ai-ide-integration',
    steps: DEFAULT_GUIDED_SETUP_STEPS,
    routes: ['/settings/ai-ide-integration'],
    requiresFlag: 'aiIdeIntegration',
    hostDriven: true,
  },
];

/**
 * Does `url` sit on or under `prefix`, matched by path SEGMENT?
 *
 * Segment-wise rather than `startsWith`, so `/risks/scan` is not treated as being under
 * `/risks/sca`. Query string and fragment are stripped first — `?group=true` does not change
 * which page you are on.
 */
export function tourRouteMatches(prefix: string, url: string): boolean {
  const path = url.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';
  const target = prefix.replace(/\/+$/, '') || '/';
  return path === target || path.startsWith(`${target}/`);
}

/** The tour whose route claims `url`, or `null`. At most one by construction (invariant 2). */
export function tourForRoute(url: string): TourDefinition | null {
  return TOUR_REGISTRY.find((t) => t.routes.some((r) => tourRouteMatches(r, url))) ?? null;
}
